import {
    NumberInput,
    TextInput,
    Textarea,
    Button,
    Switch,
    TagsInput,
    Box,
    useMantineColorScheme,
    ColorInput,
    ActionIcon,
    Text,
    Stack,
    Divider,
    Group,
    Select,
    Checkbox,
    Tabs,
    SimpleGrid,
} from '@mantine/core';
import { Trash, Plus, Settings2, Database, ListTodo, ShieldCheck, Palette } from 'lucide-react';
import type { DataSource, TableSchema, FormField } from '@bi-studio/shared/lib';
import { RoleSelector } from '@/components/common/RoleSelector';
import { BaseContainer, ConfigTabs, ConfigTabItem } from '../common';

interface FormCreateViewProps {
    activeTab: string | null;
    setActiveTab: (tab: string | null) => void;
    name: string;
    setName: (val: string) => void;
    description: string;
    setDescription: (val: string) => void;
    backgroundImage: string;
    setBackgroundImage: (val: string) => void;
    backgroundColor: string;
    setBackgroundColor: (val: string) => void;
    isPublic: boolean;
    setIsPublic: (val: boolean) => void;
    rateLimitEnabled: boolean;
    setRateLimitEnabled: (val: boolean) => void;
    rateLimitCount: number;
    setRateLimitCount: (val: number) => void;
    rateLimitWindow: number;
    setRateLimitWindow: (val: number) => void;
    fields: FormField[];
    setFields: (val: FormField[]) => void;
    allowedRoles: string[];
    setAllowedRoles: (val: string[]) => void;
    dataSources: DataSource[];
    selectedDataSource: string | null;
    setSelectedDataSource: (val: string | null) => void;
    tables: TableSchema[];
    selectedTable: string | null;
    setSelectedTable: (val: string | null) => void;
    loadingDataSources: boolean;
    loadingTables: boolean;
    isSaving: boolean;
    updateField: (index: number, updates: Partial<FormField>) => void;
    removeField: (index: number) => void;
}

export function FormCreateView({
    activeTab,
    setActiveTab,
    name,
    setName,
    description,
    setDescription,
    backgroundImage,
    setBackgroundImage,
    backgroundColor,
    setBackgroundColor,
    isPublic,
    setIsPublic,
    rateLimitEnabled,
    setRateLimitEnabled,
    rateLimitCount,
    setRateLimitCount,
    rateLimitWindow,
    setRateLimitWindow,
    fields,
    setFields,
    allowedRoles,
    setAllowedRoles,
    dataSources,
    selectedDataSource,
    setSelectedDataSource,
    tables,
    selectedTable,
    setSelectedTable,
    loadingDataSources,
    loadingTables,
    isSaving,
    updateField,
    removeField,
}: FormCreateViewProps) {
    const { colorScheme } = useMantineColorScheme();

    const addField = () => {
        setFields([...fields, {
            name: `field_${fields.length + 1}`,
            label: `New Field ${fields.length + 1}`,
            type: 'text',
            required: false,
            order: fields.length,
        }]);
    };

    const tabs: ConfigTabItem[] = [
        { value: "general", label: "General", icon: <Settings2 size={14} /> },
        { value: "data", label: "Data Source", icon: <Database size={14} /> },
        { value: "fields", label: "Fields", icon: <ListTodo size={14} /> },
        { value: "access", label: "Access", icon: <ShieldCheck size={14} /> },
        { value: "style", label: "Style", icon: <Palette size={14} /> },
    ];

    return (
        <BaseContainer>
            <Box p={{ base: 'xs', sm: 'md' }}>
                <ConfigTabs
                    value={activeTab}
                    onChange={setActiveTab}
                    tabs={tabs}
                >
                    <Tabs.Panel value="general">
                        <Stack gap="md">
                            <TextInput
                                label="Form Name"
                                placeholder="My Data Entry Form"
                                description="The name used to identify this form in the workspace."
                                required
                                value={name}
                                onChange={(e) => setName(e.currentTarget.value)}
                            />
                            <Textarea
                                label="Description"
                                placeholder="Optional description of this form"
                                description="A brief explanation of what this form is for."
                                value={description}
                                onChange={(e) => setDescription(e.currentTarget.value)}
                            />
                            <Switch
                                label="Public Access"
                                description="Allow anyone with the link to submit this form without logging in"
                                checked={isPublic}
                                onChange={(e) => setIsPublic(e.currentTarget.checked)}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="data">
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            <Select
                                label="Database"
                                placeholder={loadingDataSources ? "Loading databases..." : "Select a database source"}
                                data={dataSources.map((ds) => ({ value: ds.id, label: ds.name }))}
                                value={selectedDataSource}
                                onChange={setSelectedDataSource}
                                required
                                disabled={loadingDataSources}
                                description={dataSources.length === 0 && !loadingDataSources ? "No data sources found. Please add one in settings." : "Select the target database for submissions."}
                            />
                            <Select
                                label="Target Table"
                                placeholder={loadingTables ? "Loading tables..." : "Select a table"}
                                data={tables.map((t) => ({ value: t.name, label: t.name }))}
                                value={selectedTable}
                                onChange={setSelectedTable}
                                required
                                disabled={!selectedDataSource || loadingTables}
                                description="Submissions will be inserted into this table."
                            />
                        </SimpleGrid>
                    </Tabs.Panel>

                    <Tabs.Panel value="fields">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Box>
                                    <Text fw={600} size="sm">Form Field Mapping</Text>
                                    <Text size="xs" c="dimmed">
                                        Configure how form inputs map to your database columns.
                                    </Text>
                                </Box>
                                <Button
                                    variant="light"
                                    color="gray"
                                    leftSection={<Plus size={16} />}
                                    onClick={addField}
                                    size="xs"
                                >
                                    Add Custom Field
                                </Button>
                            </Group>

                            <Stack gap="sm">
                                {fields.map((field, index) => (
                                    <Box
                                        key={index}
                                        p="md"
                                        style={{
                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                                            borderRadius: 'var(--mantine-radius-sm)',
                                            border: '1px solid var(--modal-border)'
                                        }}
                                    >
                                        <Stack gap="xs">
                                            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                                                <TextInput
                                                    label="DB Column"
                                                    value={field.name}
                                                    onChange={(e) => updateField(index, { name: e.currentTarget.value })}
                                                    disabled // Keep name synced with table
                                                />
                                                <TextInput
                                                    label="Label"
                                                    value={field.label}
                                                    onChange={(e) => updateField(index, { label: e.currentTarget.value })}
                                                />
                                                <Select
                                                    label="Type"
                                                    data={[
                                                        { value: 'text', label: 'Text' },
                                                        { value: 'number', label: 'Number' },
                                                        { value: 'date', label: 'Date' },
                                                        { value: 'checkbox', label: 'Checkbox' },
                                                        { value: 'select', label: 'Dropdown' },
                                                        { value: 'multiselect', label: 'Multi Select' },
                                                    ]}
                                                    value={field.type}
                                                    onChange={(val) => updateField(index, { type: val || 'text' })}
                                                />
                                            </SimpleGrid>

                                            <SimpleGrid cols={{ base: 1, sm: (field.type === 'select' || field.type === 'multiselect') ? 2 : 1 }} spacing="sm">
                                                {(field.type === 'select' || field.type === 'multiselect') && (
                                                    <TagsInput
                                                        label="Options"
                                                        placeholder="Type and press Enter"
                                                        value={field.options || []}
                                                        onChange={(options) => updateField(index, { options })}
                                                        clearable
                                                    />
                                                )}
                                                <Group justify="space-between" align="center" h="100%">
                                                    <Checkbox
                                                        label="Required Field"
                                                        checked={field.required}
                                                        onChange={(e) => updateField(index, { required: e.currentTarget.checked })}
                                                    />
                                                    <ActionIcon color="red" variant="subtle" onClick={() => removeField(index)} size="lg">
                                                        <Trash size={18} />
                                                    </ActionIcon>
                                                </Group>
                                            </SimpleGrid>
                                        </Stack>
                                    </Box>
                                ))}

                                {fields.length === 0 && (
                                    <Box
                                        p="xl"
                                        style={{
                                            border: '1px dashed var(--modal-border)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Text c="dimmed" size="sm">
                                            Select a table in the <b>Data Source</b> tab to auto-generate fields, or add them manually.
                                        </Text>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="access">
                        <Stack gap="md">
                            <RoleSelector
                                value={allowedRoles}
                                onChange={setAllowedRoles}
                                disabled={isSaving}
                            />
                            <Divider label="Rate Limiting" labelPosition="center" />
                            <Switch
                                label="Enable Rate Limiting"
                                description="Prevent spam by limiting the number of submissions per user/device."
                                checked={rateLimitEnabled}
                                onChange={(e) => setRateLimitEnabled(e.currentTarget.checked)}
                            />
                            {rateLimitEnabled && (
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                    <NumberInput
                                        label="Limit"
                                        description="Max allowed submissions."
                                        value={rateLimitCount}
                                        onChange={(val) => setRateLimitCount(Number(val) || 1)}
                                        min={1}
                                    />
                                    <NumberInput
                                        label="Window (seconds)"
                                        description="Reset period for the limit."
                                        value={rateLimitWindow}
                                        onChange={(val) => setRateLimitWindow(Number(val) || 1)}
                                        min={1}
                                    />
                                </SimpleGrid>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="style">
                        <Stack gap="md">
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            <TextInput
                                label="Background Image URL"
                                placeholder="https://example.com/background.jpg"
                                description="Apply a custom background image to the form."
                                value={backgroundImage}
                                onChange={(e) => setBackgroundImage(e.currentTarget.value)}
                            />
                            <ColorInput
                                label="Background Color"
                                placeholder="Pick a fallback color"
                                description="Solid background color if no image is provided."
                                value={backgroundColor}
                                onChange={setBackgroundColor}
                            />
                        </SimpleGrid>
                        </Stack>
                    </Tabs.Panel>
                </ConfigTabs>
            </Box>
        </BaseContainer>
    );
}
